import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowRight, Clock, ArrowLeft } from "lucide-react";
import { fetchPost, fetchPosts } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { toFa } from "@/lib/fa";

export default function BlogPost() {
  const { slug } = useParams();
  const { data, isLoading } = useQuery({ queryKey: ["post", slug], queryFn: () => fetchPost(slug) });
  const allPosts = useQuery({ queryKey: ["posts"], queryFn: fetchPosts });
  const p = data?.data;

  if (isLoading) return <div className="mx-auto h-[50vh] max-w-4xl animate-pulse rounded-3xl bg-navy-850/50" />;
  if (!p)
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-5 pt-32 text-center">
        <p className="font-display text-2xl font-bold text-gold">مقاله یافت نشد</p>
        <Link to="/blog" className="btn-gold" data-testid="post-notfound-back">بازگشت به وبلاگ</Link>
      </div>
    );

  const related = (allPosts.data?.data ?? []).filter((x) => x.slug !== slug).slice(0, 3);

  return (
    <article className="pb-24 pt-28" data-testid="blog-post-page">
      <div className="mx-auto max-w-4xl px-5 lg:px-8">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-gold" data-testid="post-back-link">
          <ArrowRight size={15} />بازگشت به وبلاگ
        </Link>

        <motion.div initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
          <div className="mt-8 flex flex-wrap items-center gap-3 text-xs">
            <span className="rounded-full bg-gold/15 px-3 py-1.5 font-bold text-gold">{p.category}</span>
            <span className="text-slate-500">{p.date}</span>
            <span className="flex items-center gap-1.5 text-slate-500"><Clock size={12} />{toFa(p.readMinutes)} دقیقه مطالعه</span>
          </div>
          <h1 className="mt-5 text-3xl font-black leading-[1.5] sm:text-4xl">{p.title}</h1>
          <p className="mt-5 rounded-2xl border-r-2 border-gold/50 bg-navy-900/60 p-5 text-sm leading-8 text-slate-300">{p.excerpt}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.15 }} className="relative mt-10 overflow-hidden rounded-3xl">
          <img src={p.image} alt={p.title} className="img-treatment h-72 w-full object-cover sm:h-96" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-950/60 to-transparent" />
        </motion.div>

        <Reveal className="mt-10 space-y-6">
          {p.content?.map((c, i) => (
            <p key={i} className="text-base leading-9 text-slate-300" data-testid={`post-paragraph-${i + 1}`}>{c.text}</p>
          ))}
        </Reveal>

        <Reveal className="mt-12">
          <div className="glass-panel flex flex-col items-center gap-4 rounded-3xl border border-gold/25 p-8 text-center">
            <h3 className="font-display text-lg font-extrabold">سوالی درباره شرایط خودتان دارید؟</h3>
            <p className="max-w-md text-sm leading-7 text-slate-400">کارشناسان ما ۲۴ ساعته در خدمت شما هستند؛ همین حالا مشاوره رایگان خود را ثبت کنید.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/contact" className="btn-gold" data-testid="post-consult-cta">مشاوره رایگان</Link>
              <Link to="/tracking" className="btn-ghost" data-testid="post-tracking-cta">رهگیری پرونده</Link>
            </div>
          </div>
        </Reveal>

        <Reveal className="mt-14">
          <h3 className="mb-6 font-display text-lg font-bold text-slate-400">مطالب مرتبط</h3>
          <div className="grid gap-4 sm:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to={`/blog/${r.slug}`} className="card-glow group rounded-2xl border border-gold/15 bg-navy-850 p-5" data-testid={`post-related-${r.slug}`}>
                <span className="text-[11px] font-bold text-gold">{r.category}</span>
                <p className="mt-2 text-sm font-bold leading-6 text-slate-200 transition-colors group-hover:text-gold">{r.title}</p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-xs text-gold">مطالعه<ArrowLeft size={12} /></span>
              </Link>
            ))}
          </div>
        </Reveal>
      </div>
    </article>
  );
}
